// features/app-sidebar.js — SIDEBAR (V2)
// ═══════════════════════════════════════════════════════════
// Navigation only. A TV remote — changes the channel, never
// knows what is on each channel. See ARCHITECTURE.md.
// ═══════════════════════════════════════════════════════════
// ── SIDEBAR RULE — LOCKED ─────────────────────────────────
// This file is a navigation shell only.
//
// OWNS: icon display, active state, badge counts,
//       thread history (display only), onNavigate,
//       onNewThread, onLoadThread callbacks to shell
//
// NEVER OWNS: feature logic, messages state, API calls
//             (except loading thread history), business logic,
//             feature-specific state
//
// The sidebar never knows what a feature does.
// It only knows which feature is active.
// All navigation goes through the shell (pages/index.js):
//   Sidebar → tells shell → shell switches feature
//   Sidebar never talks directly to a feature
//
// If you find yourself adding feature logic here — STOP.
// It belongs in the feature island.
// ─────────────────────────────────────────────────────────
import { useState, useEffect, useRef } from 'react';
import { useI18n } from '../hooks/use-i18n';
import ThreadHistory from '../components/thread-history';
import UserMenu from '../components/user-menu';


// ── Icon components — three-state: rest #888, hover sage, active gold ──
const ic = (active, hovered) => active ? '#c8a96e' : hovered ? '#7a9e82' : '#888888';

const PlaybookIcon = ({ active, hovered }) => (
  <svg width="15" height="15" viewBox="0 0 18 18" fill="none">
    <rect x="3" y="1.5" width="12" height="15" rx="1.5" stroke={ic(active, hovered)} strokeWidth="1.4"/>
    <path d="M6 6h6" stroke={ic(active, hovered)} strokeWidth="1.3" strokeLinecap="round"/>
    <path d="M6 9h6" stroke={ic(active, hovered)} strokeWidth="1.3" strokeLinecap="round"/>
    <path d="M6 12h4" stroke={ic(active, hovered)} strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

const NotesIcon = ({ active, hovered }) => {
  const c = active ? '#c8a96e' : hovered ? '#7a9e82' : '#888888';
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <rect x="3" y="3" width="14" height="14" rx="2" stroke={c} strokeWidth="1.4"/>
      <path d="M6 7h8M6 10h8M6 13h5" stroke={c} strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  );
};

const SignalIcon = ({ active, hovered }) => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path d="M3 5h14M3 10h10M3 15h7" stroke={ic(active, hovered)} strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const SettingsIcon = ({ active, hovered }) => (
  <svg width="18" height="18" viewBox="0 0 15 15" fill="none">
    <circle cx="7.5" cy="7.5" r="2" stroke={ic(active, hovered)} strokeWidth="1.2"/>
    <path d="M7.5 1v1.5M7.5 12.5V14M1 7.5h1.5M12.5 7.5H14M2.9 2.9l1.1 1.1M11 11l1.1 1.1M2.9 12.1L4 11M11 4l1.1-1.1" stroke={ic(active, hovered)} strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

const ArtifactsIcon = ({ active, hovered }) => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke={ic(active, hovered)} strokeWidth="1.3">
    <rect x="2" y="2" width="12" height="12" rx="2" />
    <path d="M10 2v12" />
  </svg>
);

// Space icon (formerly Workspace) — two stylized human figures
// side by side, signaling that Space is for solo OR with others.
// Renamed 2026-04-19 from Workspace to Space.
const WorkspaceIcon = ({ active, hovered }) => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={ic(active, hovered)} strokeWidth="1.4">
    {/* Left figure (you) — slightly larger, foreground */}
    <circle cx="7" cy="7" r="2.4" />
    <path d="M2.5 17c0-2.3 2-4 4.5-4s4.5 1.7 4.5 4" strokeLinecap="round" />
    {/* Right figure (other) — slightly smaller, background */}
    <circle cx="14" cy="8" r="2" />
    <path d="M11 17c0-1.9 1.4-3.4 3-3.4s3 1.5 3 3.4" strokeLinecap="round" />
  </svg>
);

// Domain Intelligence icon — diamond-in-circle, mirrors the ◈ glyph
// used in the icon strip inside the feature itself.
const IntelligenceIcon = ({ active, hovered }) => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={ic(active, hovered)} strokeWidth="1.3">
    <circle cx="10" cy="10" r="7.5" />
    <path d="M10 4l4 6-4 6-4-6z" />
  </svg>
);

// Projects icon — flask + spark, signaling "experiments and trials."
// Different visual mass than Workstation (monitor) so the user can
// distinguish "professional work" vs "trying things out."
const ProjectsIcon = ({ active, hovered }) => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={ic(active, hovered)} strokeWidth="1.3">
    {/* flask body */}
    <path d="M8 3 L8 8 L4 16 Q4 18 6 18 L14 18 Q16 18 16 16 L12 8 L12 3" strokeLinejoin="round" />
    {/* flask top rim */}
    <path d="M7 3 L13 3" strokeLinecap="round" />
    {/* bubble */}
    <circle cx="10" cy="13" r="1.2" fill={ic(active, hovered)} stroke="none" />
  </svg>
);

// Lab icon — atom (3 rotated electron orbits around a nucleus).
// Signals "research / cross-domain investigation." Distinct from
// Projects (flask = experiments) because Lab is the discipline
// surface — case set, hypothesis registry, dossier puller, Phase A
// red-team — applied across any domain (Continuum, SavorIQ, QQQ, ...).
// Added 2026-04-30.
const LabIcon = ({ active, hovered }) => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={ic(active, hovered)} strokeWidth="1.3">
    <ellipse cx="10" cy="10" rx="8" ry="3" />
    <ellipse cx="10" cy="10" rx="8" ry="3" transform="rotate(60 10 10)" />
    <ellipse cx="10" cy="10" rx="8" ry="3" transform="rotate(-60 10 10)" />
    <circle cx="10" cy="10" r="1.5" fill={ic(active, hovered)} stroke="none" />
  </svg>
);

// Workstation icon — monitor with a chart line inside.
// Signals "the screen where data + work happens." Phase 1+2
// (2026-04) — stand and chart drawn in 3 strokes, all share
// the same hover/active color via ic().
const WorkstationIcon = ({ active, hovered }) => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={ic(active, hovered)} strokeWidth="1.3">
    {/* monitor body */}
    <rect x="2" y="3" width="16" height="11" rx="1.5" />
    {/* chart line inside */}
    <path d="M5 11 L8 8 L11 10 L15 6" strokeLinecap="round" strokeLinejoin="round" />
    {/* stand */}
    <path d="M7 17 L13 17" strokeLinecap="round" />
    <path d="M10 14 L10 17" />
  </svg>
);

const CollapseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <rect x="2" y="2" width="16" height="16" rx="3" stroke="#d4d4d4" strokeWidth="1.4"/>
    <path d="M8 2v16" stroke="#d4d4d4" strokeWidth="1.4"/>
  </svg>
);

const InviteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <circle cx="8" cy="6" r="3" stroke="#888" strokeWidth="1.4"/>
    <path d="M2 17c0-3.31 2.69-6 6-6" stroke="#888" strokeWidth="1.4" strokeLinecap="round"/>
    <path d="M15 11v6M12 14h6" stroke="#888" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const NewThreadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <path d="M10 4v12M4 10h12" stroke="#888" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <circle cx="8.5" cy="8.5" r="5" stroke="#888" strokeWidth="1.4"/>
    <path d="M13 13l3.5 3.5" stroke="#888" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

// ── Nav config ────────────────────────────────────────────
// NOTE (Phase 1+2 cleanup, 2026-04): Widgets, Digest, Innovate, Space,
// Ideas, Feedback removed. Widgets/Digest/Innovate had panels deleted;
// Space → Workspace merge in progress (Workspace is the unified surface);
// Ideas data type still lives under Notes (note_type='idea'); Feedback
// was a placeholder icon with no implementation.
// Lab is admin-only during the Professional-template transition.
// The methodology workspace consolidates into Projects → Professional
// template (see docs/PROFESSIONAL_TEMPLATE_SPEC.md §IX). Tim retains
// access to the legacy Lab page during the migration window so he
// can compare Lab vs Professional. Once Lab data is fully migrated
// the Lab entry can be removed entirely.
const ADMIN_EMAIL = 'timmai1004@gmail.com';

function buildNavLeft(userRecord) {
  const isAdmin = userRecord?.email === ADMIN_EMAIL;
  return [
    { key: 'intelligence', label: 'Intelligence', Icon: IntelligenceIcon },
    { key: 'workstations', label: 'Workstation',  Icon: WorkstationIcon },
    { key: 'projects',     label: 'Projects',     Icon: ProjectsIcon },
    isAdmin && { key: 'lab', label: 'Lab', Icon: LabIcon },
    { key: 'workspace',    label: 'Space',        Icon: WorkspaceIcon },
    { key: 'playbook',     label: 'Playbook',     Icon: PlaybookIcon },
  ].filter(Boolean);
}

const NAV_RIGHT = [
  { key: 'artifacts', label: 'Artifacts', Icon: ArtifactsIcon },
  { key: 'notes',    label: 'Notes',    Icon: NotesIcon },
  { key: 'signal',   label: 'Signal',   Icon: SignalIcon },
];

// ── NavButton ─────────────────────────────────────────────
function NavButton({ label, Icon, active, badge, onClick, collapsed }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'none',
        border: '1px solid transparent',
        borderRadius: 6,
        cursor: 'pointer',
        padding: collapsed ? '8px 0' : '8px 10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: collapsed ? 0 : '10px',
        width: '100%',
        transition: 'all 0.15s',
        position: 'relative',
        appearance: 'none',
        WebkitAppearance: 'none',
        outline: 'none',
        boxShadow: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span style={{ display: 'flex' }}>
        <Icon active={active} hovered={hovered} />
      </span>
      {!collapsed && (
        <span className="nav-label" style={{
          color: active ? '#c8a96e' : hovered ? '#7a9e82' : '#888888',
          fontSize: '14px',
          fontFamily: 'Georgia, serif',
          transition: 'color 0.15s',
        }}>
          {label}
        </span>
      )}
      {!collapsed && badge > 0 && (
        <span style={{
          position: 'absolute', right: 8, top: 6,
          background: '#4a7a4a', color: '#fff',
          fontSize: '10px', borderRadius: '50%',
          width: 16, height: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {badge > 9 ? '9+' : badge}
        </span>
      )}
      {collapsed && (
        <span className="sidebar-tooltip" style={{
          position: 'absolute',
          left: '100%',
          top: '50%',
          transform: 'translateY(-50%)',
          marginLeft: 8,
          background: '#1e1e2a',
          border: '0.5px solid #2a2a2a',
          borderRadius: 6,
          padding: '4px 10px',
          color: '#d4d4d4',
          fontSize: 12,
          fontFamily: 'Georgia, serif',
          letterSpacing: '0.04em',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          opacity: hovered ? 1 : 0,
          transition: 'opacity 0.15s',
          zIndex: 1000,
        }}>
          {label}
        </span>
      )}
    </button>
  );
}

// ── AppSidebar ────────────────────────────────────────────
export default function AppSidebar({ activeFeature, onNavigate, userRecord, supabase, badges, threadHistory, currentSessionId, onNewThread, onNewIncognitoThread, onLoadThread, onDeleteThread, onRenameThread, onRegenerateTitle, onSearch, collapsed, onToggleCollapse, onSettingsOpen, onInvite, onProfileOpen }) {
  const { t } = useI18n();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showCapture, setShowCapture] = useState(false);
  // Quick Capture always saves to Notes (with note_type='idea' on the
  // backend to preserve the data classification). Ideas as a separate
  // panel was deleted in Phase 1+2 cleanup; the type stays under Notes.
  // Note: the save handler below is currently a no-op placeholder; wire
  // it to /api/user/notes when promoting Quick Capture beyond a stub.
  const [incognitoHover, setIncognitoHover] = useState(false);
  const [captureHovered, setCaptureHovered] = useState(false);
  const [userIconHovered, setUserIconHovered] = useState(false);
  const [showCollapsedUserMenu, setShowCollapsedUserMenu] = useState(false);
  const capturePopupRef = useRef(null);
  const userMenuPopupRef = useRef(null);

  useEffect(() => {
    if (!showCapture && !showCollapsedUserMenu) return;
    function handleClick(e) {
      if (showCapture && collapsed && capturePopupRef.current && !capturePopupRef.current.contains(e.target)) {
        setShowCapture(false);
      }
      if (showCollapsedUserMenu && userMenuPopupRef.current && !userMenuPopupRef.current.contains(e.target)) {
        setShowCollapsedUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showCapture, showCollapsedUserMenu, collapsed]);

  const handleNav = (feature) => {
    onNavigate(feature);
    setMobileOpen(false);
  };

  const renderColumn = (items) =>
    items.map(({ key, label, Icon }) => (
      <NavButton
        key={key}
        label={label}
        Icon={Icon}
        active={activeFeature === key}
        badge={badges[key] || 0}
        onClick={() => handleNav(key)}
        collapsed={collapsed}
      />
    ));

  return (
    <>
      {/* Backdrop — mobile only */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 59, background: 'rgba(0,0,0,0.5)' }}
        />
      )}

      <div
        className={`shift-sidebar${mobileOpen ? ' open' : ''}`}
        style={{
          flexShrink: 0,
          transition: 'width 0.2s ease',
          overflow: collapsed ? 'visible' : 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          padding: collapsed ? '20px 0 12px' : '20px 16px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          flexShrink: 0,
        }}>
          {!collapsed && (
            <span style={{ color: '#ffffff', fontSize: '26px', letterSpacing: '-0.01em', fontFamily: 'Georgia, serif', fontWeight: 'bold' }}>
              Shift
            </span>
          )}
          <button
            onClick={() => { onToggleCollapse?.(); setMobileOpen(false); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, opacity: 0.35, lineHeight: 1 }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '0.35'; }}
          >
            <CollapseIcon />
          </button>
        </div>

        <div style={{ height: 1, background: '#272736', margin: '0 8px', flexShrink: 0 }} />

        {/* ── Action rows: Invite/Search + New thread/Incognito (2x2 grid) ── */}
        {!collapsed && (
          <div style={{ padding: '4px 8px 4px', flexShrink: 0 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>

              {/* Invite */}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', border: '1px solid transparent' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#07070a'; e.currentTarget.querySelector('.al').style.color = '#7a9e82'; e.currentTarget.querySelector('svg').style.stroke = '#7a9e82'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.querySelector('.al').style.color = '#888888'; e.currentTarget.querySelector('svg').style.stroke = '#888888'; }}
                onClick={() => onInvite?.()}
              >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ stroke: '#888888', flexShrink: 0 }}>
                  <path d="M13 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM2 17c0-3.314 3.134-6 7-6s7 2.686 7 6" strokeWidth="1.4" strokeLinecap="round"/>
                  <path d="M16 8v4M18 10h-4" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
                <span className="al" style={{ fontSize: 14, fontFamily: 'Georgia, serif', color: '#888888' }}>Invite</span>
              </div>

              {/* Search */}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', border: '1px solid transparent' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#07070a'; e.currentTarget.querySelector('.al').style.color = '#7a9e82'; e.currentTarget.querySelector('svg').style.stroke = '#7a9e82'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.querySelector('.al').style.color = '#888888'; e.currentTarget.querySelector('svg').style.stroke = '#888888'; }}
                onClick={onSearch}
              >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ stroke: '#888888', flexShrink: 0 }}>
                  <circle cx="9" cy="9" r="6" strokeWidth="1.4"/>
                  <path d="M17 17l-3.5-3.5" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
                <span className="al" style={{ fontSize: 14, fontFamily: 'Georgia, serif', color: '#888888' }}>Search</span>
              </div>

              {/* New thread */}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', border: '1px solid transparent' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#07070a'; e.currentTarget.querySelector('.al').style.color = '#7a9e82'; e.currentTarget.querySelector('svg').style.stroke = '#7a9e82'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.querySelector('.al').style.color = '#888888'; e.currentTarget.querySelector('svg').style.stroke = '#888888'; }}
                onClick={() => { onNewThread?.(); handleNav('chat'); }}
              >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ stroke: '#888888', flexShrink: 0 }}>
                  <path d="M10 4v12M4 10h12" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span className="al" style={{ fontSize: 14, fontFamily: 'Georgia, serif', color: '#888888' }}>New thread</span>
              </div>

              {/* Incognito */}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', border: '1px solid transparent' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#07070a'; e.currentTarget.querySelector('.al').style.color = '#7a9e82'; e.currentTarget.querySelector('svg').style.stroke = '#7a9e82'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.querySelector('.al').style.color = '#888888'; e.currentTarget.querySelector('svg').style.stroke = '#888888'; }}
                onClick={() => { onNewIncognitoThread?.(); handleNav('chat'); }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ stroke: '#888888', flexShrink: 0 }}>
                  <path d="M12 3a7 7 0 0 1 7 7v7l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5-2 1.5v-7a7 7 0 0 1 7-7z" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="9" cy="11" r="1" fill="#888888"/>
                  <circle cx="15" cy="11" r="1" fill="#888888"/>
                </svg>
                <span className="al" style={{ fontSize: 14, fontFamily: 'Georgia, serif', color: '#888888' }}>Incognito</span>
              </div>

            </div>
          </div>
        )}

        {!collapsed && <div style={{ height: 1, background: '#272736', margin: '0 8px', flexShrink: 0 }} />}

        {/* ── Nav grid ── */}
        <div style={{ padding: collapsed ? '8px 4px' : '8px', flexShrink: 0 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: collapsed ? '1fr' : '1fr 1fr',
            gap: 2,
          }}>
            {(() => {
              const navLeft = buildNavLeft(userRecord);
              return collapsed ? (
                <div>{renderColumn([...navLeft, ...NAV_RIGHT])}</div>
              ) : (
                <>
                  <div>{renderColumn(navLeft)}</div>
                  <div>{renderColumn(NAV_RIGHT)}</div>
                </>
              );
            })()}
          </div>
        </div>

        <div style={{ height: 1, background: '#272736', margin: '0 8px', flexShrink: 0 }} />


        {/* ── Thread history ── */}
        {!collapsed && (
          <>
            <div style={{ padding: '8px 16px 4px', flexShrink: 0 }}>
              <span style={{ color: '#3a3a3a', fontSize: '10px', letterSpacing: '0.14em', textTransform: 'uppercase', fontFamily: 'Georgia, serif' }}>
                Recents
              </span>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '2px 8px 8px', scrollbarWidth: 'none' }}>
              <ThreadHistory
                threadHistory={threadHistory}
                currentSessionId={currentSessionId}
                onLoadThread={(id, isIncognito) => { onLoadThread?.(id, isIncognito); handleNav('chat'); }}
                onDeleteThread={onDeleteThread}
                onRenameThread={onRenameThread}
                onRegenerateTitle={onRegenerateTitle}
              />
            </div>
          </>
        )}

        {collapsed && <div style={{ flex: 1 }} />}

        {/* Quick Capture */}
        <div style={{ borderTop: '1px solid #272736', flexShrink: 0 }}>
          {showCapture ? (
            collapsed ? (
              <div ref={capturePopupRef} style={{
                position: 'fixed', left: 54, bottom: 60, width: 280, zIndex: 300,
                background: '#111111', borderRadius: 10, border: '1px solid #2a2a2a',
                boxShadow: '0 4px 24px rgba(0,0,0,0.4)', overflow: 'hidden',
              }}>
                <div style={{ padding: '12px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ color: '#c8a96e', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Georgia, serif' }}>Quick Capture</span>
                    <button onClick={() => setShowCapture(false)} style={{ background: 'none', border: 'none', color: '#444', fontSize: 16, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
                  </div>
                  <div
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder="Catch it before it disappears..."
                    id="sidebar-capture-input"
                    style={{ background: '#07070a', border: '1px solid #222', borderRadius: 8, padding: 10, minHeight: 70, color: '#d4d4d4', fontSize: 13, fontFamily: 'Georgia, serif', outline: 'none', lineHeight: 1.6, marginBottom: 10 }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => {
                        const el = document.getElementById('sidebar-capture-input');
                        const text = el?.innerText?.trim();
                        if (text) { el.innerText = ''; }
                        setShowCapture(false);
                      }}
                      style={{ background: 'rgba(122,158,130,0.1)', border: '1px solid rgba(122,158,130,0.25)', borderRadius: 20, color: '#7a9e82', fontSize: 11, fontFamily: 'Georgia, serif', padding: '5px 12px', cursor: 'pointer' }}
                    >
                      Save to Notes
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ padding: '12px 10px', background: '#111', borderRadius: '10px 10px 0 0', border: '1px solid #2a2a2a', borderBottom: 'none', margin: '0 4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ color: '#c8a96e', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Georgia, serif' }}>Quick Capture</span>
                  <button onClick={() => setShowCapture(false)} style={{ background: 'none', border: 'none', color: '#444', fontSize: 16, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
                </div>
                <div
                  contentEditable
                  suppressContentEditableWarning
                  data-placeholder="Catch it before it disappears..."
                  id="sidebar-capture-input"
                  style={{ background: '#07070a', border: '1px solid #222', borderRadius: 8, padding: 10, minHeight: 70, color: '#d4d4d4', fontSize: 13, fontFamily: 'Georgia, serif', outline: 'none', lineHeight: 1.6, marginBottom: 10 }}
                />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      const el = document.getElementById('sidebar-capture-input');
                      const text = el?.innerText?.trim();
                      if (text) { el.innerText = ''; }
                      setShowCapture(false);
                    }}
                    style={{ background: 'rgba(122,158,130,0.1)', border: '1px solid rgba(122,158,130,0.25)', borderRadius: 20, color: '#7a9e82', fontSize: 11, fontFamily: 'Georgia, serif', padding: '5px 12px', cursor: 'pointer' }}
                  >
                    Save to Notes
                  </button>
                </div>
              </div>
            )
          ) : (
            !collapsed ? (
              <button
                onClick={() => setShowCapture(true)}
                style={{ background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 10, width: '100%', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(122,158,130,0.04)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
              >
                <div
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(122,158,130,0.12)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: 'transparent', border: '1px solid #7a9e82',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, transition: 'background 0.15s',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 21h6M12 3a6 6 0 0 1 4 10.5V17H8v-3.5A6 6 0 0 1 12 3z" stroke="#7a9e82" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span style={{ color: '#7a9e82', fontSize: 13, fontFamily: 'Georgia, serif' }}>Quick Capture</span>
              </button>
            ) : (
              <button
                onClick={() => { setShowCapture(true); }}
                onMouseEnter={() => setCaptureHovered(true)}
                onMouseLeave={() => setCaptureHovered(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}
              >
                <div
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(122,158,130,0.12)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: 'transparent', border: '1px solid #7a9e82',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, transition: 'background 0.15s',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 21h6M12 3a6 6 0 0 1 4 10.5V17H8v-3.5A6 6 0 0 1 12 3z" stroke="#7a9e82" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span style={{
                  position: 'absolute', left: '100%', top: '50%', transform: 'translateY(-50%)',
                  marginLeft: 8, background: '#1e1e2a', border: '1px solid #2a2a2a', borderRadius: 6,
                  padding: '4px 10px', color: '#888', fontSize: 11, fontFamily: 'Georgia, serif',
                  whiteSpace: 'nowrap', zIndex: 1000, pointerEvents: 'none',
                  opacity: captureHovered && !showCapture ? 1 : 0, transition: 'opacity 0.15s',
                }}>
                  Quick Capture
                </span>
              </button>
            )
          )}
        </div>

        {/* ── User menu ── */}
        {!collapsed && (
          <UserMenu
            userRecord={userRecord}
            supabase={supabase}
            onSettingsOpen={onSettingsOpen}
            onProfileOpen={onProfileOpen}
          />
        )}
        {collapsed && (
          <div
            onMouseEnter={() => setUserIconHovered(true)}
            onMouseLeave={() => setUserIconHovered(false)}
            style={{ borderTop: '1px solid #272736', padding: '12px 0', display: 'flex', justifyContent: 'center', position: 'relative' }}
          >
            <div
              onClick={() => setShowCollapsedUserMenu(p => !p)}
              style={{ width: 36, height: 36, borderRadius: '50%', background: '#2a2520', border: '1px solid #3a3020', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <span style={{ color: '#c8a96e', fontSize: '15px', fontFamily: 'Georgia, serif', fontWeight: 'bold' }}>
                {(userRecord?.first_name || 'U')[0].toUpperCase()}
              </span>
            </div>
            <span style={{
              position: 'absolute', left: '100%', top: '50%', transform: 'translateY(-50%)',
              marginLeft: 8, background: '#1e1e2a', border: '1px solid #2a2a2a', borderRadius: 6,
              padding: '4px 10px', color: '#888', fontSize: 11, fontFamily: 'Georgia, serif',
              whiteSpace: 'nowrap', zIndex: 1000, pointerEvents: 'none',
              opacity: userIconHovered && !showCollapsedUserMenu ? 1 : 0, transition: 'opacity 0.15s',
            }}>
              {userRecord?.first_name || 'Account'}
            </span>
            {showCollapsedUserMenu && (
              <div
                ref={userMenuPopupRef}
                style={{ position: 'fixed', left: 54, bottom: 60, width: 220, zIndex: 1000 }}
              >
                <UserMenu userRecord={userRecord} supabase={supabase} onSettingsOpen={onSettingsOpen} onProfileOpen={onProfileOpen} initialOpen hideTrigger />
              </div>
            )}
          </div>
        )}
      </div>
      <style jsx>{`
        .shift-sidebar button:focus { outline: none; box-shadow: none; }
        .shift-sidebar button:focus-visible { outline: none; box-shadow: none; }
        .shift-sidebar button:active { box-shadow: none !important; }
        * { -webkit-tap-highlight-color: transparent; }
      `}</style>
    </>
  );
}
